import "./entry-group-step-2.scss";

import { FormikErrors, useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";
import { lowerLanguage } from "src/i18n";
import {
  fromGroupAsset,
  toGroupAsset,
  usePostGetGroup,
} from "src/networking/terminals";
import {
  atomDossier,
  atomEvaluatePremium,
  atomFormikGroup,
  defaultValueInsured,
  selectorDocumentsGroup,
} from "src/storage";
import { GroupFile } from "src/types/networking/terminals/group.types";
import { generateFiscalCode, useBoolean } from "src/utils";

import {
  FileUploadedStatus,
  Formik,
  FormikInsurance,
  FormikInsured,
  YesNoAnswersNew,
} from "src/types";
import { AnyObjectSchema } from "yup";
import { MAX_INSURED } from "src/config";

export const useEntryGroupStep2 = ({
  insuranceFormik,
  validationSchema,
}: {
  insuranceFormik: Formik<FormikInsurance>;
  validationSchema: AnyObjectSchema;
}) => {
  const { t } = useTranslation();

  const { setFieldValue: mainInsuredSetFieldValue } = insuranceFormik;

  const [groupInfo, setGroupInfo] = useRecoilState(atomFormikGroup);

  const resetEvaluatedPremium = useResetRecoilState(atomEvaluatePremium);
  const dossier = useRecoilValue(atomDossier);

  const [fileGroup, setFileGroup] = useState<File | undefined>();
  const [isModalVisible, showModal, hideModal] = useBoolean(false);

  const { crossGate: postGetGroup } = usePostGetGroup();

  useEffect(() => {
    if (fileGroup) {
      setTimeout(async () => {
        const groupFile: GroupFile = await formatFile(fileGroup);

        postGetGroup(
          {
            headers: {
              xDossierId: dossier?.dossierId || "",
            },
            body: {
              groupFiles: [groupFile],
              language: lowerLanguage,
            },
          },
          {
            onSuccess: (data) => {
              let status: FileUploadedStatus = data.hasErrors;

              if (data?.groups?.length > MAX_INSURED) {
                status = FileUploadedStatus.LIMIT_EXCEEDED;
              }

              if (!status) {
                const hasErrors = data.groups?.some(
                  (asset) =>
                    asset.errors && Object.keys(asset.errors).length > 0
                );
                if (!hasErrors) status = FileUploadedStatus.OK;
              }

              setGroupInfo((prev) => ({
                ...prev,
                group: data?.groups || [],
                filesStatus: status,
                fileName: groupFile.name,
              }));

              const insuredList: FormikInsured[] = [];
              data?.groups?.map((groupInsured, index) => {
                const insured: FormikInsured = fromGroupAsset(
                  groupInsured,
                  index
                );

                insuredList.push(insured);
              });

              mainInsuredSetFieldValue("insuredList", insuredList);
              mainInsuredSetFieldValue("insuredNumber", insuredList.length);
            },
          }
        );
      }, 1000);
    }
  }, [fileGroup]);

  ////////////////////////////////////////////////////////////////////////////////////////////

  const [isInsuredDuplicatedError, setIsInsuredDuplicatedError] = useState<
    String | undefined
  >();
  const [duplicatedInsuredValues, setDuplicatedInsuredValues] = useState<
    FormikInsured | undefined
  >();

  const modalFormik = useFormik<FormikInsured>({
    initialValues: defaultValueInsured,
    validationSchema: validationSchema,
    onSubmit: (values, { setTouched, setSubmitting }) => {
      setTouched({});

      mainInsuredSetFieldValue(`insuredList[${personIndexToModify}]`, values);

      let status = groupInfo.filesStatus;
      const newGroup = groupInfo.group.map((g) =>
        g.assicurato.idAssicurato === values.id ? toGroupAsset(values) : g
      );

      if (
        status !== FileUploadedStatus.OK &&
        !newGroup.some(
          (asset) => asset.errors && Object.keys(asset.errors).length > 0
        )
      ) {
        status = FileUploadedStatus.OK;
      }

      setGroupInfo((prev) => ({
        ...prev,
        group: newGroup,
        filesStatus: status,
      }));

      hideModal();

      setSubmitting(false);
    },
  });

  const documents = useRecoilValue(selectorDocumentsGroup);
  const onDownloadFile = () => {
    window.open(documents?.[0]?.filePath[lowerLanguage], "_blank");
  };

  const formatFile = async (file: File): Promise<GroupFile> => {
    const formattedFile: GroupFile = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        resolve({
          name: file?.name || "",
          size: file?.size || 0,
          data: reader.result?.toString() || "",
          type: file?.type || "",
          lastModified: file?.lastModified || 0,
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    return formattedFile;
  };

  const [personIndexToModify, setPersonIndexToModify] = useState<number>(0);
  const onShowModalEditPerson = async (index: number) => {
    setPersonIndexToModify(index);
    setIsInsuredDuplicatedError(undefined);
    setDuplicatedInsuredValues(undefined);

    const groupInsured = groupInfo.group[index];

    const insured: FormikInsured = fromGroupAsset(groupInsured, index);

    const insuredErrors: FormikErrors<FormikInsured> = {
      insuredName: groupInsured.errors?.nome,
      insuredSurname: groupInsured.errors?.cognome,
      insuredBirth: groupInsured.errors?.dataNascita,
      insuredBirthNation: groupInsured.errors?.nazioneNascita,
      insuredBirthPlace: groupInsured.errors?.luogoNascita,
      insuredFc: groupInsured.errors?.codiceFiscale,
      insuredGender: groupInsured.errors?.sesso,
      insuredFcQuestion: groupInsured.errors?.fiscalCodeQuestion,
      insuredNationality: groupInsured.errors?.nazionalita,
    };

    const insuredTouched: Partial<Record<keyof FormikInsured, boolean>> = {
      insuredName: !!groupInsured.errors?.nome,
      insuredSurname: !!groupInsured.errors?.cognome,
      insuredBirth: !!groupInsured.errors?.dataNascita,
      insuredBirthNation: !!groupInsured.errors?.nazioneNascita,
      insuredBirthPlace: !!groupInsured.errors?.luogoNascita,
      insuredFc: !!groupInsured.errors?.codiceFiscale,
      insuredGender: !!groupInsured.errors?.sesso,
      insuredFcQuestion: !!groupInsured.errors?.fiscalCodeQuestion,
      insuredNationality: !!groupInsured.errors?.nazionalita,
    };

    await modalFormik.setValues(insured, false);

    await modalFormik.setTouched(insuredTouched, false);

    modalFormik.setErrors(insuredErrors);

    if (
      insured.insuredFcQuestion === YesNoAnswersNew.NO &&
      !!insuredErrors.insuredFc
    ) {
      setDuplicatedInsuredValues(insured);
      setIsInsuredDuplicatedError(t("entry_group_step_2.duplicated_insured"));
    }

    showModal();
  };

  const onIdentificationFieldsChanges = (newInsuredValues: FormikInsured) => {
    if (!duplicatedInsuredValues) return;

    let newFiscalCode = newInsuredValues.insuredFc;
    let prevFiscalCode = duplicatedInsuredValues.insuredFc;

    if (newInsuredValues.insuredFcQuestion === YesNoAnswersNew.NO)
      newFiscalCode = "";

    if (duplicatedInsuredValues.insuredFcQuestion === YesNoAnswersNew.NO)
      prevFiscalCode = "";

    if (!newFiscalCode)
      newFiscalCode = generateFiscalCode({
        name: newInsuredValues.insuredName,
        surname: newInsuredValues.insuredSurname,
        birthDate: newInsuredValues.insuredBirth,
        birthPlace: newInsuredValues.insuredBirthPlace,
        gender: newInsuredValues.insuredGender,
      });

    if (!prevFiscalCode)
      prevFiscalCode = generateFiscalCode({
        name: duplicatedInsuredValues.insuredName,
        surname: duplicatedInsuredValues.insuredSurname,
        birthDate: duplicatedInsuredValues.insuredBirth,
        birthPlace: duplicatedInsuredValues.insuredBirthPlace,
        gender: duplicatedInsuredValues.insuredGender,
      });

    if (newFiscalCode !== prevFiscalCode) {
      setIsInsuredDuplicatedError(undefined);
      return;
    }

    setIsInsuredDuplicatedError(t("entry_group_step_2.duplicated_insured"));
  };

  const onRemoveGroupInfoFile = () => {
    resetEvaluatedPremium();
    setGroupInfo((prev) => ({
      ...prev,
      group: [],
      filesStatus: undefined,
      fileName: "",
    }));
  };

  const isGroupErrorBanner = useMemo(() => {
    switch (groupInfo.filesStatus) {
      case FileUploadedStatus.EMPTY:
      case FileUploadedStatus.FORMAT_ERROR:
      case FileUploadedStatus.LIMIT_EXCEEDED:
        return true;
      default:
        return false;
    }
  }, [groupInfo.filesStatus]);

  return {
    t,
    fileGroup,
    groupInfo,
    isModalVisible,
    modalFormik,
    isGroupErrorBanner,
    isInsuredDuplicatedError,
    hideModal,
    onRemoveGroupInfoFile,
    setFileGroup,
    onDownloadFile,
    onShowModalEditPerson,
    onIdentificationFieldsChanges,
  };
};
